/**
 * BandLogEntryForm — Compact inline form injected into AttendanceDialog
 * for teachers to record Bänder (FlexBand) execution status.
 *
 * Uses direct band/assignment/period queries — does NOT depend on the
 * heavyweight scheduling payload.
 *
 * @module baende/components/BandLogEntryForm
 */

import React, { useMemo, useState, useEffect, useImperativeHandle, forwardRef, useCallback } from "react";
import { toast } from "sonner";
import { useAuth } from "../../../contexts/AuthContext";
import {
  useGetBaendeForSchoolQuery,
  useGetBandLogEntriesQuery,
} from "../api/queries";
import { useGetBandClassAssignmentsQuery } from "../api/assignmentQueries";
import { useSaveBandLogEntryMutation } from "../api/mutations";
import { schedulingApi } from "../../scheduling/api/schedulingApi";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { Checkbox } from "../../../components/ui/checkbox";
import { CheckCircle2, Save, Loader2, Pencil } from "lucide-react";
import { BandIcon } from "./BandIcon";
import type { BandLogStatus } from "../types/baendeTypes";

interface BandLogEntryFormProps {
  lessonId: string;
  classId: string;
  /** ISO datetime string (e.g. "2026-03-23T10:35:00") */
  dateStr: string;
  /** Period/block number from schedule_periods.block_number — used to match the correct band assignment */
  periodNumber?: number | null;
  /**
   * When true, the lesson is in the future: show the band as "planned" with
   * the Verschoben / Ausgefallen pre-planning actions but hide Durchgeführt
   * (you can't confirm something that hasn't happened yet).
   */
  futureMode?: boolean;
  /**
   * Pre-fetched band data from the attendance bundle. When provided, the form
   * skips its 4 RPC queries entirely and reads bands/assignments/periods/logs
   * from these arrays. Shape mirrors the legacy fn_baende_* / schedule_periods
   * responses so existing matching logic stays unchanged.
   */
  bundledBandData?: {
    bands: any[];
    assignments: any[];
    periods: any[];
    logs: any[];
  };
}

/** Result returned from {@link BandLogEntryFormHandle.triggerSave}. Lets the
 * parent (the attendance dialog) decide whether to proceed with its own save
 * if the band log save failed or was incomplete. */
export interface BandLogTriggerSaveResult {
  ok: boolean;
  /** Coarse failure reason — `incomplete` (form not filled), `missing-moved-to`
   * (status=moved without target period), or `rpc-error` (save call failed). */
  reason?: "incomplete" | "missing-moved-to" | "missing-required-field" | "rpc-error";
}

/** Handle exposed via ref so parents can trigger save externally */
export interface BandLogEntryFormHandle {
  triggerSave: () => Promise<BandLogTriggerSaveResult>;
}

/** Format "08:00:00" → "08:00" */
function fmtTime(t: string | undefined) {
  return t?.slice(0, 5) ?? "";
}

export const BandLogEntryForm = forwardRef<BandLogEntryFormHandle, BandLogEntryFormProps>(
  function BandLogEntryForm({
    lessonId,
    classId,
    dateStr,
    periodNumber,
    futureMode = false,
    bundledBandData,
  }, ref) {
  const { schoolId } = useAuth();

  const lessonDate = useMemo(() => new Date(dateStr), [dateStr]);
  const dayOfWeek = lessonDate.getDay(); // 0=Sun … 6=Sat
  const dateIso = dateStr.slice(0, 10);

  // When the parent passes pre-fetched band data (from the attendance bundle)
  // we skip every individual RPC. Falls back to the legacy fetches otherwise.
  const useBundled = !!bundledBandData;

  // ── 1. Fetch bands with fields ───────────────────────────────────────
  const { data: fetchedBands = [], isLoading: isBandsLoadingRPC } =
    useGetBaendeForSchoolQuery(undefined as any, { skip: useBundled });
  const bands = useBundled ? (bundledBandData!.bands as any[]) : (fetchedBands as any[]);
  const isBandsLoading = useBundled ? false : isBandsLoadingRPC;

  // ── 2. Fetch assignments for this class ──────────────────────────────
  const { data: fetchedAssignments = [], isLoading: isAssignmentsLoadingRPC } = useGetBandClassAssignmentsQuery(
    { classId, date: dateIso },
    { skip: useBundled || !classId },
  );
  const assignments = useBundled ? (bundledBandData!.assignments as any[]) : (fetchedAssignments as any[]);
  const isAssignmentsLoading = useBundled ? false : isAssignmentsLoadingRPC;

  // ── 3. Fetch schedule periods (for Verschoben picker) ────────────────
  const { data: fetchedPeriods = [] } = schedulingApi.useGetPeriodsForSchedulingQuery(
    { schoolId: schoolId! },
    { skip: useBundled || !schoolId },
  );
  const periods = useBundled ? (bundledBandData!.periods as any[]) : (fetchedPeriods as any[]);

  // ── 4. Fetch existing logs for this day ──────────────────────────────
  const dayStart = dateIso + "T00:00:00Z";
  const dayEnd = dateIso + "T23:59:59Z";
  const { data: fetchedLogs = [], isLoading: isLogsLoadingRPC } =
    useGetBandLogEntriesQuery({
      start_date: dayStart,
      end_date: dayEnd,
      class_id: classId,
    }, { skip: useBundled });
  const logs = useBundled ? (bundledBandData!.logs as any[]) : (fetchedLogs as any[]);
  const isLogsLoading = useBundled ? false : isLogsLoadingRPC;

  const [saveLogEntry, { isLoading: isSaving }] = useSaveBandLogEntryMutation();

  // ── Derive time/period from lesson's start_time ──────────────────────
  const lessonTime = useMemo(() => dateStr.slice(11, 16), [dateStr]); // "HH:mm"

  const resolvedPeriodNumber = useMemo(() => {
    if (periodNumber != null) return periodNumber;
    const match = periods.find((p: any) => (p.start_time ?? "").slice(0, 5) === lessonTime);
    return (match as any)?.block_number ?? null;
  }, [periodNumber, periods, lessonTime]);

  // ── Derive this lesson's period UUID from start_time ─────────────────
  const currentPeriodId = useMemo(() => {
    const match = periods.find((p: any) => (p.start_time ?? "").slice(0, 5) === lessonTime);
    return (match as any)?.id ?? null;
  }, [periods, lessonTime]);

  // ── Match assignment for own slot (day + period) ─────────────────────
  const assignment = useMemo(() => {
    return (
      assignments.find((a) => {
        const dayMatch = a.day_of_week === dayOfWeek;
        const periodMatch = resolvedPeriodNumber != null
          ? a.period_number === resolvedPeriodNumber
          : true;
        return dayMatch && periodMatch;
      }) ?? null
    );
  }, [assignments, dayOfWeek, resolvedPeriodNumber]);

  const band = useMemo(() => {
    if (!assignment) return null;
    return bands.find((b) => b.id === assignment.band_id) ?? null;
  }, [bands, assignment]);

  // ── Detect if a band was Verschoben TO this lesson ───────────────────
  const movedHereLog = useMemo(() => {
    if (!currentPeriodId) return null;
    return logs.find((l) => l.moved_to_period_id === currentPeriodId) ?? null;
  }, [logs, currentPeriodId]);

  const movedHereAssignment = useMemo(() => {
    if (!movedHereLog) return null;
    return assignments.find((a) => a.assignment_id === movedHereLog.assignment_id) ?? null;
  }, [movedHereLog, assignments]);

  const movedHereBand = useMemo(() => {
    if (!movedHereAssignment) return null;
    return bands.find((b) => b.id === movedHereAssignment.band_id) ?? null;
  }, [bands, movedHereAssignment]);

  // ── Active assignment/band ────────────────────────────────────────────
  const activeAssignment = assignment ?? movedHereAssignment;
  const activeBand = band ?? movedHereBand;
  const isMovedHere = !assignment && !!movedHereLog;

  // Debug logs — only emitted in DEV builds so production logs stay clean
  // and we don't leak lesson/class identifiers to Sentry breadcrumbs.
  if (import.meta.env.DEV) {
    console.log("🎸 PROPS:", { lessonId, classId, dateStr, dayOfWeek, dateIso, lessonTime, resolvedPeriodNumber, currentPeriodId });
    console.log("🎸 ASSIGNMENTS:", assignments.length, JSON.stringify(assignments.slice(0, 3)));
    console.log("🎸 LOGS:", logs.length, "movedHereLog:", movedHereLog?.assignment_id ?? null);
    console.log("🎸 ACTIVE BAND:", activeBand ? { id: activeBand.id, name: activeBand.name } : null);
  }

  // Instructional periods for the "moved to" picker — exclude the current
  // displayed period since moving the band to where it already is is a no-op.
  const instructionalPeriods = useMemo(() => {
    return periods
      .filter((p: any) => p.block_type === "instructional")
      .filter((p: any) => p.id !== currentPeriodId)
      .sort((a: any, b: any) => a.block_number - b.block_number);
  }, [periods, currentPeriodId]);

  // At the origin slot: is there a log saying the band was moved away?
  // Used to suppress rendering here — the band is being handled at its target.
  const movedOutLog = useMemo(() => {
    if (!assignment) return null;
    return (
      logs.find(
        (l) =>
          l.assignment_id === assignment.assignment_id &&
          l.lesson_id === lessonId &&
          l.status === "moved",
      ) ?? null
    );
  }, [logs, assignment, lessonId]);

  // At the moved-here slot we do NOT surface the origin's "moved" log — that
  // log describes the move, not the outcome at the target. Instead look for a
  // separate target-local outcome log keyed at the target's own lesson_id.
  // When the teacher saves Durchgeführt/Ausgefallen here it becomes a new row,
  // leaving the origin's moved log intact. Picking "Verschoben" + a new
  // target at the target slot updates the origin's moved log (handled in the
  // save path), keeping exactly one moved log per chain.
  const existingLog = useMemo(() => {
    if (isMovedHere) {
      return (
        logs.find(
          (l) =>
            l.assignment_id === activeAssignment?.assignment_id &&
            l.lesson_id === lessonId &&
            l.status !== "moved",
        ) ?? undefined
      );
    }
    if (!assignment) return undefined;
    return logs.find(
      (l) => l.assignment_id === assignment.assignment_id && l.lesson_id === lessonId,
    );
  }, [logs, assignment, activeAssignment, lessonId, isMovedHere]);


  // ── Local state ──────────────────────────────────────────────────────
  const [status, setStatus] = useState<BandLogStatus | "">("");
  const [fieldValues, setFieldValues] = useState<Record<string, any>>({});
  const [movedToStunde, setMovedToStunde] = useState<string>("");
  const [saved, setSaved] = useState(false);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (existingLog) {
      setStatus(existingLog.status);
      const fv = existingLog.field_values || {};
      setFieldValues(fv);
      setMovedToStunde(existingLog.moved_to_period_id ?? "");
      setSaved(true);
    } else {
      setFieldValues({});
      // At the moved-here slot, seed the Verschoben picker with the current
      // move target so the teacher can chain-move by changing it, or pick the
      // origin to undo via the RPC auto-reset path.
      setMovedToStunde(isMovedHere ? (movedHereLog?.moved_to_period_id ?? "") : "");
      setStatus("");
      setSaved(false);
    }
  }, [existingLog, isMovedHere, movedHereLog]);


  const markDirty = () => setSaved(false);

  const handleSave = useCallback(async (): Promise<BandLogTriggerSaveResult> => {
    if (!activeAssignment?.assignment_id || !lessonId || !classId || !status) {
      return { ok: false, reason: "incomplete" };
    }

    if (status === "moved" && !movedToStunde) {
      toast.error("Bitte wählen Sie die Stunde, in die verschoben wird.");
      return { ok: false, reason: "missing-moved-to" };
    }

    // Enforce is_required band fields when marking Durchgeführt. Empty =
    // no value for dropdown/text, unchecked for checkbox.
    if (status === "completed") {
      const fields = activeBand?.fields ?? [];
      const missing = fields.find((f: any) => {
        if (!f.is_required) return false;
        const v = fieldValues[f.field_name];
        if (f.field_type === "checkbox") return v !== true && v !== "true";
        return typeof v !== "string" || v.trim() === "";
      });
      if (missing) {
        toast.error(`Bitte "${missing.field_name}" ausfüllen.`);
        return { ok: false, reason: "missing-required-field" };
      }
    }

    const allValues = { ...fieldValues };
    // Save routing:
    //   • status=moved at the moved-here slot → update the ORIGIN's moved log
    //     (so a chain-move doesn't create a second moved row and picking the
    //     origin lets the RPC auto-undo delete exactly that row).
    //   • anything else → current slot's lesson_id, either upserting an
    //     existing log at this slot or creating a fresh outcome log.
    const effectiveLessonId =
      status === "moved" && isMovedHere && movedHereLog
        ? movedHereLog.lesson_id
        : existingLog?.lesson_id ?? lessonId;

    try {
      await saveLogEntry({
        assignment_id: activeAssignment.assignment_id,
        lesson_id: effectiveLessonId,
        class_id: classId,
        field_values: allValues,
        status: status as BandLogStatus,
        moved_to_period_id: status === "moved" ? movedToStunde : null,
      }).unwrap();
      setSaved(true);
      setEditing(false);
      return { ok: true };
    } catch (err) {
      if (import.meta.env.DEV) console.error("Failed to save band log:", err);
      toast.error("Fehler beim Speichern des Band-Eintrags.");
      return { ok: false, reason: "rpc-error" };
    }
  }, [activeAssignment, activeBand, lessonId, classId, status, movedToStunde, fieldValues, saveLogEntry, isMovedHere, movedHereLog, existingLog]);

  // Expose triggerSave to parent via ref
  useImperativeHandle(ref, () => ({
    triggerSave: handleSave,
  }), [handleSave]);

  // ── Guard ───────────────────────────────────────────────────────────────
  if (import.meta.env.DEV) {
    console.log("🎸 GUARD CHECK:", {
      isBandsLoading, isAssignmentsLoading, isLogsLoading,
      bandsCount: bands.length,
      assignmentsCount: assignments.length,
      logsCount: logs.length,
      dayOfWeek,
      resolvedPeriodNumber,
      assignmentFound: !!assignment,
      bandFound: !!band,
      activeAssignment: activeAssignment ? { id: activeAssignment.assignment_id, day: activeAssignment.day_of_week, period: activeAssignment.period_number } : null,
      activeBand: activeBand ? { id: activeBand.id, name: activeBand.name } : null,
      existingLogStatus: existingLog?.status ?? "none",
    });
  }

  // Show loading spinner while queries are in flight
  if (isBandsLoading || isAssignmentsLoading || isLogsLoading) {
    return (
      <div className="flex items-center gap-2 px-3 py-3 text-xs text-gray-400">
        <Loader2 className="h-3 w-3 animate-spin" />
        Lade Band-Daten…
      </div>
    );
  }
  // Band was moved away from this slot — hide it. The band is rendered at its
  // target slot instead; un-doing the move happens by saving "Durchgeführt" at
  // the target or via the auto-undo RPC path (selecting origin in the picker).
  if (assignment && movedOutLog) {
    return null;
  }
  // No matching band at all
  if (!activeAssignment || !activeBand) {
    return null;
  }

  const bandFields = activeBand.fields ?? [];

  const isAlreadySaved = !!existingLog;

  // ── Summary mode (saved & not editing) ──────────────────────────────────
  if (saved && !editing) {
    const statusLabel = status === "completed" ? "✅ Durchgeführt"
      : status === "moved" ? "↗ Verschoben"
      : status === "not_done" ? "✗ Ausgefallen" : "";

    // Collect field summaries (without Notizen — shown on second line)
    const chips: string[] = [];
    for (const f of bandFields) {
      const val = fieldValues[f.field_name];
      if (f.field_type === "checkbox" && (val === true || val === "true")) {
        chips.push(f.field_name);
      } else if (f.field_type === "dropdown" && val) {
        chips.push(`${f.field_name}: ${val}`);
      }
    }

    return (
      <div
        className="rounded-md border overflow-hidden my-3"
        style={{ borderColor: `${activeBand.color}40` }}
      >
        <div
          className="px-3 py-2 flex items-center justify-between"
          style={{ backgroundColor: `${activeBand.color}08` }}
        >
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <BandIcon
              iconName={activeBand.icon}
              color={activeBand.color}
              completed={status === "completed"}
              size={14}
            />
            <span className="text-xs font-medium" style={{ color: activeBand.color }}>
              {activeBand.name}
            </span>
            <span className="text-xs text-gray-500">—</span>
            <span className="text-xs font-medium text-gray-700">{statusLabel}</span>
            {chips.length > 0 && (
              <span className="text-xs text-gray-400 truncate">
                · {chips.join(" · ")}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
              title="Bearbeiten"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
        {(typeof fieldValues?.Notizen === "string" &&
          (fieldValues.Notizen as string).trim().length > 0) && (
          <div className="px-3 py-1.5 text-xs text-gray-500 italic border-t border-gray-100">
            {fieldValues.Notizen as string}
          </div>
        )}
      </div>
    );
  }

  // ── Edit mode ───────────────────────────────────────────────────────────
  return (
    <div
      className="rounded-md border overflow-hidden my-3"
      style={{ borderColor: `${activeBand.color}40` }}
    >
      {/* Header */}
      <div
        className="px-3 py-2 flex items-center justify-between"
        style={{ backgroundColor: `${activeBand.color}10` }}
      >
        <div className="flex items-center gap-2">
          <BandIcon
            iconName={activeBand.icon}
            color={activeBand.color}
            completed={isAlreadySaved && existingLog?.status === "completed"}
            size={16}
          />
          <span className="text-sm font-medium" style={{ color: activeBand.color }}>
            {activeBand.name}
          </span>
        </div>

        {isAlreadySaved && (
          <span className="flex items-center gap-1 text-xs text-green-600">
            <CheckCircle2 className="h-3 w-3" />
            Gespeichert
          </span>
        )}
      </div>

      {/* Body */}
      <div className="px-3 py-2 space-y-2">
        {/* Status pills */}
        <div className="flex items-center gap-1.5 flex-wrap">
          {(futureMode
            ? ([
                // Future / pre-plan wording: infinitive actions the teacher
                // is about to take, not past-tense states.
                { value: "moved", label: "↗ Verschieben", selectedLabel: "↗ Verschieben" },
                { value: "not_done", label: "✗ Absagen", selectedLabel: "✗ Absagen" },
              ] as const)
            : ([
                { value: "completed", label: "Durchgeführt", selectedLabel: "✅ Durchgeführt" },
                { value: "moved", label: "↗ Verschoben", selectedLabel: "↗ Verschoben" },
                { value: "not_done", label: "✗ Ausgefallen", selectedLabel: "✗ Ausgefallen" },
              ] as const)
          ).map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => {
                setStatus((prev) => prev === opt.value ? "" : opt.value as BandLogStatus);
                markDirty();
              }}
              className={`px-2 py-1 text-xs rounded border transition-colors ${
                status === opt.value
                  ? "bg-blue-50 border-blue-300 text-blue-700 font-medium"
                  : "bg-white border-gray-200 text-gray-500 hover:bg-gray-50"
              }`}
            >
              {status === opt.value ? opt.selectedLabel : opt.label}
            </button>
          ))}
        </div>

        {/* Verschoben → period picker */}
        {status === "moved" && (
          <div className="flex items-center gap-2 text-xs">
            <span className="text-gray-500 whitespace-nowrap">
              {futureMode ? "Verschieben auf:" : "Verschoben auf:"}
            </span>
            <Select
              value={movedToStunde}
              onValueChange={(v) => {
                setMovedToStunde(v);
                markDirty();
              }}
            >
              <SelectTrigger className="h-7 text-xs flex-1">
                <SelectValue placeholder="Stunde wählen…" />
              </SelectTrigger>
              <SelectContent>
                {instructionalPeriods.map((p: any) => (
                  <SelectItem key={p.id} value={p.id} className="text-xs">
                    {p.label || `${p.block_number}. Stunde`} (
                    {fmtTime(p.start_time)}–{fmtTime(p.end_time)})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Dynamic band fields — only when Durchgeführt */}
        {status === "completed" && bandFields.length > 0 && (
          <div className="space-y-2 pt-1 border-t border-gray-100">
            {[...bandFields]
              .sort((a, b) => a.display_order - b.display_order)
              .map((field) => (
                <div key={field.id} className="flex items-center gap-2">
                  {field.field_type === "dropdown" && field.options && (
                    <>
                      <span className="text-xs text-gray-500 whitespace-nowrap">
                        {field.field_name}
                        {field.is_required ? " *" : ""}:
                      </span>
                      <Select
                        value={
                          (fieldValues[field.field_name] as string) || ""
                        }
                        onValueChange={(val) => {
                          setFieldValues((prev) => ({
                            ...prev,
                            [field.field_name]: val,
                          }));
                          markDirty();
                        }}
                      >
                        <SelectTrigger className="h-7 text-xs flex-1">
                          <SelectValue placeholder="Wählen…" />
                        </SelectTrigger>
                        <SelectContent>
                          {field.options.map((opt: any) => (
                            <SelectItem
                              key={opt}
                              value={opt}
                              className="text-xs"
                            >
                              {opt}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </>
                  )}

                  {field.field_type === "checkbox" && (
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <Checkbox
                        id={`cb-${field.id}`}
                        checked={
                          fieldValues[field.field_name] === true ||
                          fieldValues[field.field_name] === "true"
                        }
                        onCheckedChange={(c) => {
                          setFieldValues((prev) => ({
                            ...prev,
                            [field.field_name]: c,
                          }));
                          markDirty();
                        }}
                        className="h-3.5 w-3.5"
                      />
                      <span className="text-xs text-gray-600">
                        {field.field_name}
                        {field.is_required ? " *" : ""}
                      </span>
                    </label>
                  )}

                  {field.field_type === "text" && (
                    <>
                      <span className="text-xs text-gray-500 whitespace-nowrap">
                        {field.field_name}
                        {field.is_required ? " *" : ""}:
                      </span>
                      <input
                        type="text"
                        value={
                          (fieldValues[field.field_name] as string) || ""
                        }
                        onChange={(e) => {
                          setFieldValues((prev) => ({
                            ...prev,
                            [field.field_name]: e.target.value,
                          }));
                          markDirty();
                        }}
                        className="flex-1 h-7 px-2 text-xs border rounded"
                        placeholder="…"
                      />
                    </>
                  )}
                </div>
              ))}
          </div>
        )}


        {/* Save button */}
        {status && !saved && (
          <div className="flex justify-end">
            <button
              onClick={handleSave}
              disabled={isSaving || isLogsLoading}
              className="flex items-center gap-1 px-3 py-1 text-xs font-medium text-white rounded transition-colors disabled:opacity-50"
              style={{ backgroundColor: activeBand.color }}
            >
              {isSaving ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Save className="h-3 w-3" />
              )}
              Speichern
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
);
